/**
 * Phase 14 Plan 14-02 — TanStack Query v5 hooks for the Logsheet Approver feature.
 *
 * Mirrors the `useLogsheetCheckerQueries` pattern from Plan 13-02:
 *  - Typed query/mutation hooks over the `logsheet-approver.ts` service layer.
 *  - `staleTime: 30_000` on list queries; `staleTime: 5_000` on detail query.
 *  - Mutations invalidate the relevant query keys on success.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/api/error';
import {
  approverApproveRequest,
  approverBulkApproveRequest,
  approverRejectRequest,
  getApproverHistoryCsvRequest,
  getApproverHistoryRequest,
  getApproverPendingDetailRequest,
  getApproverPendingRequest,
  getApproverRecentEntriesRequest,
} from '@/services/logsheet-approver';
import type {
  ApproverApproveRequest,
  ApproverBulkApproveRequest,
  ApproverBulkApproveResult,
  ApproverDetail,
  ApproverHistoryItem,
  ApproverHistoryListQuery,
  ApproverPendingItem,
  ApproverPendingListQuery,
  ApproverRecentEntry,
  ApproverRejectRequest,
} from '@/types/logsheet-approver';
import type { PaginatedResult } from '@/types/roles';

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

/** Paginated queue of Checked entries for the current approver. */
export function useApproverPendingQuery(query: ApproverPendingListQuery = {}) {
  return useQuery<PaginatedResult<ApproverPendingItem>, ApiError>({
    queryKey: ['approver', 'pending', query],
    queryFn: () => getApproverPendingRequest(query),
    staleTime: 30_000,
  });
}

/** Full detail for a single pending entry (approver view). */
export function useApproverDetailQuery(id: number) {
  return useQuery<ApproverDetail, ApiError>({
    queryKey: ['approver', 'detail', id],
    queryFn: () => getApproverPendingDetailRequest(id),
    enabled: Number.isFinite(id) && id > 0,
    staleTime: 5_000,
  });
}

/** Last-5 historical entries for the same machine (comparison panel). */
export function useApproverRecentEntriesQuery(id: number) {
  return useQuery<readonly ApproverRecentEntry[], ApiError>({
    queryKey: ['approver', 'recent', id],
    queryFn: () => getApproverRecentEntriesRequest(id),
    enabled: Number.isFinite(id) && id > 0,
    staleTime: 30_000,
  });
}

/** Approver's own action history (Approved + Rejected). */
export function useApproverHistoryQuery(query: ApproverHistoryListQuery = {}) {
  return useQuery<PaginatedResult<ApproverHistoryItem>, ApiError>({
    queryKey: ['approver', 'history', query],
    queryFn: () => getApproverHistoryRequest(query),
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

interface ApproveVars {
  readonly id: number;
  readonly body: ApproverApproveRequest;
}

/** Approve a single Checked entry (Checked → Approved). */
export function useApproverApproveMutation() {
  const qc = useQueryClient();
  return useMutation<ApproverDetail, ApiError, ApproveVars>({
    mutationFn: ({ id, body }) => approverApproveRequest(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['approver', 'pending'] });
      void qc.invalidateQueries({ queryKey: ['approver', 'history'] });
      // Round-trip: approval changes entry state that checker history may reflect
      void qc.invalidateQueries({ queryKey: ['checker', 'pending'] });
      // REVIEW FF-030: PendingForMeWidget approverCount drifts otherwise.
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

interface RejectVars {
  readonly id: number;
  readonly body: ApproverRejectRequest;
}

/**
 * Reject a Checked entry with reason + remarks + routeTo.
 * RouteTo=Maker → Status=Rejected; RouteTo=Checker → Status=Submitted (back to checker queue).
 */
export function useApproverRejectMutation() {
  const qc = useQueryClient();
  return useMutation<ApproverDetail, ApiError, RejectVars>({
    mutationFn: ({ id, body }) => approverRejectRequest(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['approver', 'pending'] });
      void qc.invalidateQueries({ queryKey: ['approver', 'history'] });
      // RouteTo=Checker: entry goes back into checker queue — invalidate checker pending
      void qc.invalidateQueries({ queryKey: ['checker', 'pending'] });
      // REVIEW FF-030: PendingForMeWidget counts depend on approver state.
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

/** Bulk-approve up to 100 pending entries (P3). */
export function useApproverBulkApproveMutation() {
  const qc = useQueryClient();
  return useMutation<ApproverBulkApproveResult, ApiError, ApproverBulkApproveRequest>({
    mutationFn: (body) => approverBulkApproveRequest(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['approver', 'pending'] });
      void qc.invalidateQueries({ queryKey: ['approver', 'history'] });
      // REVIEW FF-030: bulk-approve flips many entries from Checked → Approved
      // — dashboard counts must refetch.
      void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Non-hook helpers
// ---------------------------------------------------------------------------

/**
 * Download approver history as a CSV file.
 * Fetches the blob then triggers a synthetic anchor click for browser download.
 * Does NOT use TanStack Query (one-shot imperative action, not reactive state).
 */
export async function downloadHistoryCsv(query: ApproverHistoryListQuery = {}): Promise<void> {
  const blob = await getApproverHistoryCsvRequest(query);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'approver-history.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
